import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Select, Store } from "@ngxs/store";
import { Observable } from "rxjs";

import { BeginTotpSetupDto, MfaService, MfaSettingsDto } from "@ha/feature/mfa";
import { SignInCommand } from "../../auth/models/sign-in-command";
import { SettingsStateActions } from "../../core/states/settings.actions";
import { SettingsState } from "../../core/states/settings.state";

@Component({
    selector: "app-login-mfa-setup",
    templateUrl: "./login-mfa-setup.component.html",
    styleUrls: ["./login-mfa-setup.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
}) @UntilDestroy()
export class LoginMfaSetupComponent implements OnInit {
    @Select(SettingsState.currentLanguage)
    public currentLanguage$: Observable<string>;

    @Select(SettingsState.uiLanguages)
    public uiLanguages$: Observable<string[]>;

    public loading = true;
    public saving = false;
    public error = "";
    public qrLoading = true;
    public qrError = false;

    public settings?: MfaSettingsDto;
    public setup?: BeginTotpSetupDto;

    public originalSignInCommand?: SignInCommand;
    public returnUrl?: string;
    public step: "code" | "verify" = "code";

    public form = this.fb.group({
        verificationCode: ["", [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });

    public constructor(
        private readonly fb: FormBuilder,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly mfaService: MfaService,
        private readonly cdr: ChangeDetectorRef,
        private readonly store: Store,
    ) {
    }

    public ngOnInit(): void {
        this.loading = true;

        const storedCredentials = sessionStorage.getItem("mfa-setup-credentials");
        if (storedCredentials) {
            this.originalSignInCommand = JSON.parse(storedCredentials);
        }

        const qpReturnUrl = this.route.snapshot.queryParams["returnUrl"];
        if (qpReturnUrl) {
            this.returnUrl = qpReturnUrl as string;
        }

        this.startMfaSetup();
    }

    public startMfaSetup(): void {
        const userId = this.route.snapshot.queryParams["Id"];

        if (!userId) {
            console.error("No userId provided for MFA setup");
            this.error = "errors.INVALID_SETUP_REQUEST";
            this.loading = false;
            this.qrLoading = false;
            this.qrError = true;
            return;
        }

        this.mfaService.beginSetupForUser(userId).pipe(untilDestroyed(this)).subscribe({
            next: (setup) => {
                this.setup = setup;
                this.loading = false;
                this.qrLoading = false;
                this.qrError = false;

                this.cdr.detectChanges();

                setTimeout(() => {
                    if (this.setup?.qrCodePngBase64) {
                        this.qrLoading = false;
                        this.qrError = false;
                        this.cdr.detectChanges();
                    }
                }, 200);
            },
            error: (err) => {
                console.error("MFA setup error:", err);
                this.error = "errors.UNKNOWN";
                this.loading = false;
                this.qrLoading = false;
                this.qrError = true;
                this.cdr.detectChanges();
            },
        });
    }

    public onQrImageLoad(): void {
        this.qrLoading = false;
        this.qrError = false;
        this.cdr.markForCheck();
    }

    public onQrImageError(): void {
        this.qrLoading = false;
        this.qrError = true;
        this.cdr.markForCheck();
    }

    public async copyKey(): Promise<void> {
        if (!this.setup?.sharedKey) {
            return;
        }

        try {
            await navigator.clipboard.writeText(this.setup.sharedKey);
        } catch (err) {
            console.error("Copy failed:", err);
        }
    }

    public verify(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.saving = true;
        this.error = "";
        const code = this.form.value.verificationCode!;
        const userId = this.route.snapshot.queryParams["Id"];

        if (!userId) {
            this.error = "errors.INVALID_SETUP_REQUEST";
            this.saving = false;
            this.cdr.detectChanges();
            return;
        }

        this.mfaService.confirmSetupForUser(code, userId).pipe(untilDestroyed(this)).subscribe({
            next: (result) => {
                if (result.autoLoginRequired) {
                    this.completeMfaSetupAndLogin(userId, code);
                } else {
                    const code = result.errorCode;
                    this.error = code ? `errors.${code}` : "errors.UNKNOWN";
                    this.saving = false;
                    this.cdr.detectChanges();
                }
            },
            error: (err) => {
                console.error("MFA setup confirmation error:", err);
                const code = err?.error?.errorCode;
                this.error = code ? `errors.${code}` : "errors.INVALID_VERIFICATION_CODE";
                this.saving = false;
                this.cdr.detectChanges();
            },
        });
    }

    private completeMfaSetupAndLogin(userId: string, twoFactorCode: string): void {
        this.mfaService.completeMfaSetup(userId, twoFactorCode).pipe(untilDestroyed(this)).subscribe({
            next: (result) => {
                if (result.status === 1) {
                    this.saving = false;
                    this.cdr.detectChanges();

                    sessionStorage.removeItem("mfa-setup-credentials");

                    const dest = this.returnUrl || this.originalSignInCommand?.returnUrl || "/";
                    window.location.href = dest;
                } else {
                    const code = (result as any)?.errorCode;
                    this.error = code ? `errors.${code}` : "errors.UPDATE_FAILED";
                    this.saving = false;
                    this.cdr.detectChanges();
                }
            },
            error: (err) => {
                console.error("Complete MFA setup error:", err);
                const code = err?.error?.errorCode;
                this.error = code ? `errors.${code}` : "errors.UPDATE_FAILED";
                this.saving = false;
                this.cdr.detectChanges();
            },
        });
    }

    public cancel(): void {
        sessionStorage.removeItem("mfa-setup-credentials");
        this.router.navigate(["/login"]);
    }

    public languageChanged(languageCode: string): void {
        this.store.dispatch(new SettingsStateActions.ChangeLanguage(languageCode));
    }
}
